/**
 * User Enrichment Service
 *
 * Signal-based service that enriches FGA user IDs with profile data
 * from Auth0 Management API. Provides caching for performance and
 * graceful fallbacks when enrichment is unavailable.
 *
 * @packageDocumentation
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, catchError, of, timeout, retry } from 'rxjs';
import {
  Auth0UserProfile,
  EnrichedUser,
  fgaToAuth0UserId
} from '../models/auth0-user.model';
import { Auth0OrgMember } from '../models/auth0-org.model';
import { ApiHttpError } from '../models/api.model';

/**
 * Authorization error from user enrichment API calls.
 * Tracked when Auth0 Management API returns 401/403.
 *
 * In future, multiple enrichment methods may exist (e.g., direct Auth0 API,
 * cached user store, etc.). The error should only be surfaced when ALL
 * methods fail with auth errors.
 */
export interface UserEnrichmentAuthError {
  /**
   * HTTP status code (401 for unauthenticated, 403 for forbidden)
   */
  status: 401 | 403;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Timestamp when error occurred (Unix milliseconds)
   */
  timestamp: number;

  /**
   * Original error details if available
   */
  details?: unknown;
}

/**
 * Provider of organisation member data for enrichment.
 * Abstracts Auth0OrganisationService so UserEnrichmentService
 * doesn't depend on the concrete class (which is element-scoped).
 */
export interface OrgMembersProvider {
  /**
   * Whether the provider has been configured with API settings.
   * Compatible with Angular Signal<boolean> (callable returning boolean).
   */
  configured: () => boolean;

  /**
   * Fetch organisation members using the best available method.
   * Returns Auth0OrgMember[] with user_id, email, name.
   *
   * @param userIds - Auth0 user IDs to fetch (used by fallback methods)
   * @param options - Optional filter settings
   */
  getOrganisationMembers(
    userIds: string[],
    options?: { filter?: string }
  ): Promise<Auth0OrgMember[]>;
}

/**
 * Authorization error from user enrichment API calls.
 * Tracked when Auth0 Management API returns 401/403.
 *
 * In future, multiple enrichment methods may exist (e.g., direct Auth0 API,
 * cached user store, etc.). The error should only be surfaced when ALL
 * methods fail with auth errors.
 */
export interface UserEnrichmentAuthError {
  /**
   * HTTP status code (401 for unauthenticated, 403 for forbidden)
   */
  status: 401 | 403;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Timestamp when error occurred (Unix milliseconds)
   */
  timestamp: number;

  /**
   * Original error details if available
   */
  details?: unknown;
}

/**
 * Configuration for the user enrichment service
 */
export interface UserEnrichmentConfig {
  /**
   * Base URL for Auth0 API (domain, e.g., "https://tenant.auth0.com")
   * The service will construct the full endpoint path for /api/v2/userinfo
   * @example "https://tenant.auth0.com"
   */
  apiBase: string;

  /**
   * Access token with userinfo scope for reading user profile
   * @example "eyJhbGciOiJSUzI1NiIsInR5..."
   */
  accessToken?: string;

  /**
   * Subscription key for Azure API Management (ocp-apim-subscription-key header)
   * Required when the Auth0 Management Proxy requires subscription key authentication.
   * @example "abc123def456"
   */
  subscriptionKey?: string;

  /**
   * Request timeout in milliseconds
   * @default 5000
   */
  timeout?: number;

  /**
   * Number of retry attempts on failure
   * @default 2
   */
  retries?: number;

  /**
   * When true (default), the service appends `/api/v2` to the configured base URL.
   * Set to `false` when providing a proxy URL that already includes the `/api/v2` segment
   * (for example, `http://localhost:3001/api/v2`).
   * @default true
   */
  appendApiPath?: boolean;
}

/**
 * Internal cache entry with metadata
 */
interface CacheEntry {
  profile: Auth0UserProfile | null;
  fetchedAt: number;
  error?: string;
}

/**
 * Service for enriching FGA user IDs with Auth0 profile data.
 *
 * This service:
 * - Converts FGA user IDs to Auth0 format
 * - Fetches user profiles from Auth0 Management API using access token
 * - Caches results in memory for performance
 * - Provides fallback display names when enrichment fails
 *
 * Auth0 API Endpoint: `GET https://{tenant}.auth0.com/api/v2/users/{id}`
 * Requires: Access token with `read:users` scope
 *
 * @example
 * ```typescript
 * const enrichment = inject(UserEnrichmentService);
 * enrichment.configure({
 *   apiBase: 'https://tenant.auth0.com',
 *   accessToken: 'your-access-token-here'
 * });
 *
 * // Enrich single user
 * await enrichment.enrichUser('user:waad|org_demo|abc123');
 *
 * // Get display value (cached)
 * const email = enrichment.getDisplayValue('user:waad|org_demo|abc123');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class UserEnrichmentService {
  private readonly http = inject(HttpClient);

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Private State
  // ---------------------------------------------------------------------------

  /**
   * Cache of FGA user ID → Auth0 profile
   */
  private readonly _cache = signal<Map<string, CacheEntry>>(new Map());

  /**
   * Set of user IDs currently being fetched
   */
  private readonly _pendingFetches = new Set<string>();

  /**
   * Last authorization error (401/403) from enrichment API.
   * Cleared on successful API call.
   */
  private readonly _lastAuthError = signal<UserEnrichmentAuthError | null>(null);

  /**
   * Service configuration
   */
  private _config: UserEnrichmentConfig = {
    apiBase: '',
    timeout: 5000,
    retries: 2,
    appendApiPath: true
  };

  /**
   * Whether the service is enabled/configured
   */
  private readonly _enabled = signal(false);

  /**
   * Optional organisation member provider for enhanced enrichment.
   * When set, enrichUsers() will try this provider first before
   * falling back to Lucene batch queries.
   */
  private _orgProvider: OrgMembersProvider | null = null;

  // ---------------------------------------------------------------------------
  // Public Readonly Signals
  // ---------------------------------------------------------------------------

  /**
   * Readonly signal indicating if enrichment is enabled
   */
  readonly enabled = this._enabled.asReadonly();

  /**
   * Computed signal returning the number of cached entries
   */
  readonly cacheSize = computed(() => this._cache().size);

  /**
   * Computed signal returning cache statistics
   */
  readonly cacheStats = computed(() => {
    const cache = this._cache();
    let successful = 0;
    let failed = 0;
    let notFound = 0;

    cache.forEach(entry => {
      if (entry.profile) {
        successful++;
      } else if (entry.error) {
        failed++;
      } else {
        notFound++;
      }
    });

    return { total: cache.size, successful, failed, notFound };
  });

  /**
   * Readonly signal exposing the last authorization error (401/403).
   * Returns null if no auth error has occurred or if it was cleared by a successful request.
   *
   * In future, when multiple enrichment methods exist, this will only be set
   * when ALL methods fail with auth errors.
   */
  readonly lastAuthError = this._lastAuthError.asReadonly();

  /**
   * Computed signal indicating if there's an active auth error.
   * Useful for conditionally showing unauthorized UI.
   */
  readonly hasAuthError = computed(() => this._lastAuthError() !== null);

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  /**
   * Set the organisation members provider for enhanced enrichment.
   * When set, enrichUsers() will try fetching via the organisation
   * endpoint first (most efficient), falling back to Lucene batch
   * if the org provider fails or returns incomplete data.
   *
   * Called by fga-manager component to connect the element-scoped
   * Auth0OrganisationService with this root-scoped enrichment service.
   *
   * @param provider - Organisation members provider (typically Auth0OrganisationService)
   */
  setOrgProvider(provider: OrgMembersProvider): void {
    this._orgProvider = provider;
  }

  /**
   * Configure the enrichment service.
   *
   * @param config - Service configuration
   */
  configure(config: Partial<UserEnrichmentConfig>): void {
    this._config = { ...this._config, ...config };
    this._enabled.set(!!this._config.apiBase);
  }

  /**
   * Set the API base URL
   *
   * @param apiBase - Base URL for Auth0 user API
   */
  setApiBase(apiBase: string): void {
    this._config.apiBase = apiBase.replace(/\/$/, '');
    this._enabled.set(!!apiBase);
  }

  // ---------------------------------------------------------------------------
  // Enrichment Methods
  // ---------------------------------------------------------------------------

  /**
   * Enrich a single user by FGA ID.
   * Returns immediately if cached, otherwise fetches from API.
   *
   * @param fgaUserId - FGA user identifier
   * @returns Enriched user data
   */
  async enrichUser(fgaUserId: string): Promise<EnrichedUser> {
    // Check cache first
    const cached = this._cache().get(fgaUserId);
    if (cached) {
      return this.createEnrichedUser(fgaUserId, cached.profile, cached.error);
    }

    // Skip if already fetching
    if (this._pendingFetches.has(fgaUserId)) {
      return this.createEnrichedUser(fgaUserId, null);
    }

    // Fetch from API
    if (!this._enabled()) {
      return this.createEnrichedUser(fgaUserId, null, 'Enrichment not enabled');
    }

    return this.fetchAndCacheUser(fgaUserId);
  }

  /**
   * Enrich multiple users efficiently via the organisation members provider.
   * No Lucene batch fallback — errors from the org provider are propagated
   * as `ApiHttpError` and tracked via auth error signals.
   *
   * @param fgaUserIds - Array of FGA user identifiers
   * @returns Array of enriched users
   */
  async enrichUsers(fgaUserIds: string[]): Promise<EnrichedUser[]> {
    const results: EnrichedUser[] = [];
    const toFetch: string[] = [];

    // Separate cached from uncached
    for (const fgaUserId of fgaUserIds) {
      const cached = this._cache().get(fgaUserId);
      if (cached) {
        results.push(this.createEnrichedUser(fgaUserId, cached.profile, cached.error));
      } else if (!this._pendingFetches.has(fgaUserId)) {
        toFetch.push(fgaUserId);
      }
    }

    // Use org provider to fetch uncached users
    if (toFetch.length > 0 && this._orgProvider?.configured()) {
      try {
        const auth0Ids: string[] = [];
        for (const fgaId of toFetch) {
          try { auth0Ids.push(fgaToAuth0UserId(fgaId)); } catch { /* skip invalid IDs */ }
        }
        if (auth0Ids.length > 0) {
          const orgMembers = await this._orgProvider.getOrganisationMembers(auth0Ids);
          this.populateCacheFromOrgMembers(toFetch, orgMembers);

          // Clear auth error on successful fetch
          this.clearAuthError();
        }
      } catch (error) {
        // Track auth errors (401/403) from the org provider
        if (this.isApiHttpAuthError(error)) {
          const apiError = error as ApiHttpError;
          this.setAuthError(apiError.status as 401 | 403, apiError);
        }
      }
    }

    // Build results for all users (cached + newly cached from org + placeholders)
    for (const fgaId of toFetch) {
      const cached = this._cache().get(fgaId);
      if (cached) {
        results.push(this.createEnrichedUser(fgaId, cached.profile, cached.error));
      } else {
        results.push(this.createEnrichedUser(fgaId, null));
      }
    }

    return results;
  }

  /**
   * Get the display value for a user (name > auth0UserId).
   * Returns immediately from cache, does not trigger fetch.
   *
   * @param fgaUserId - FGA user identifier
   * @returns Display string (upn/email if available, otherwise auth0UserId without user: prefix)
   */
  getDisplayName(fgaUserId: string): string {
    const cached = this._cache().get(fgaUserId);
    if (cached?.profile?.name) {
      return cached.profile.name;
    }

    // Fallback to auth0UserId (without user: prefix)
    try {
      return fgaToAuth0UserId(fgaUserId);
    } catch {
      return fgaUserId.startsWith('user:') ? fgaUserId.substring(5) : fgaUserId;
    }
  }

  /**
   * Get the display value for a user (upn > email > auth0UserId).
   * Returns immediately from cache, does not trigger fetch.
   *
   * @param fgaUserId - FGA user identifier
   * @returns Display string (upn/email if available, otherwise auth0UserId without user: prefix)
   */
  getDisplayEmail(fgaUserId: string): string {
    const cached = this._cache().get(fgaUserId);
    if (cached?.profile?.upn) {
      return cached.profile.upn;
    }
    if (cached?.profile?.email) {
      return cached.profile.email;
    }
    // Fallback to auth0UserId (without user: prefix)
    try {
      return fgaToAuth0UserId(fgaUserId);
    } catch {
      return fgaUserId.startsWith('user:') ? fgaUserId.substring(5) : fgaUserId;
    }
  }

  /**
   * Get cached profile for a user, if available.
   *
   * @param fgaUserId - FGA user identifier
   * @returns Cached profile or null
   */
  getCachedProfile(fgaUserId: string): Auth0UserProfile | null {
    return this._cache().get(fgaUserId)?.profile ?? null;
  }

  /**
   * Check if a user's profile is cached.
   *
   * @param fgaUserId - FGA user identifier
   * @returns true if cached (even if null/error)
   */
  isCached(fgaUserId: string): boolean {
    return this._cache().has(fgaUserId);
  }

  /**
   * Check if a user's profile is currently being fetched.
   *
   * @param fgaUserId - FGA user identifier
   * @returns true if fetch is in progress
   */
  isFetching(fgaUserId: string): boolean {
    return this._pendingFetches.has(fgaUserId);
  }

  // ---------------------------------------------------------------------------
  // Cache Management
  // ---------------------------------------------------------------------------

  /**
   * Clear the entire cache.
   */
  clearCache(): void {
    this._cache.set(new Map());
  }

  /**
   * Remove a specific user from cache.
   *
   * @param fgaUserId - FGA user identifier to remove
   */
  invalidateUser(fgaUserId: string): void {
    this._cache.update(cache => {
      const newCache = new Map(cache);
      newCache.delete(fgaUserId);
      return newCache;
    });
  }

  /**
   * Preload profiles for a list of users (fire-and-forget).
   * Useful for warming cache when users are known.
   *
   * @param fgaUserIds - Array of FGA user identifiers
   */
  preloadUsers(fgaUserIds: string[]): void {
    // Don't await - let this run in background
    this.enrichUsers(fgaUserIds);
  }

  /**
   * Populate the enrichment cache from Auth0 Organisation Service data.
   *
   * Bridges `Auth0OrgMember[]` (from Auth0OrganisationService) into the
   * enrichment cache so `getDisplayValue()` returns real email/name
   * without additional API calls.
   *
   * @param fgaUserIds - FGA user identifiers to map against org members
   * @param orgMembers - Pre-fetched members from Auth0OrganisationService
   * @returns Number of cache entries populated
   */
  populateCacheFromOrgMembers(
    fgaUserIds: string[],
    orgMembers: Auth0OrgMember[]
  ): number {
    // Build lookup: auth0UserId → Auth0OrgMember
    const memberByAuth0Id = new Map(
      orgMembers.map(m => [m.user_id, m])
    );

    let populated = 0;

    for (const fgaId of fgaUserIds) {
      // Skip if already cached
      if (this._cache().has(fgaId)) continue;

      try {
        const auth0Id = fgaToAuth0UserId(fgaId);
        const member = memberByAuth0Id.get(auth0Id);

        if (member) {
          // Convert Auth0OrgMember to Auth0UserProfile shape for cache
          const profile: Auth0UserProfile = {
            user_id: member.user_id,
            email: member.email,
            name: member.name
          };
          this.cacheProfile(fgaId, profile);
          populated++;
        }
        // Don't cache misses — let Lucene batch handle users not found in org data
      } catch {
        // Invalid FGA user ID format — skip silently
      }
    }

    return populated;
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  /**
   * Fetch user profile from API and cache result.
   */
  private async fetchAndCacheUser(fgaUserId: string): Promise<EnrichedUser> {
    // Mark as pending
    this._pendingFetches.add(fgaUserId);

    try {
      // Use the /userinfo endpoint with access token
      if (!this._config.accessToken) {
        return this.createEnrichedUser(fgaUserId, null, 'Access token not configured');
      }

      const trimmedBase = this._config.apiBase.replace(/\/+$/, '');
      const shouldAppendApiPath = this._config.appendApiPath ?? true;
      const baseWithoutApiPath = trimmedBase.replace(/\/api\/v2$/i, '');
      const base = shouldAppendApiPath ? baseWithoutApiPath : trimmedBase;
      const apiPath = shouldAppendApiPath ? '/api/v2' : '';
      const auth0UserId = fgaToAuth0UserId(fgaUserId);
      const url = `${base}${apiPath}/users/${encodeURIComponent(auth0UserId)}`;
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${this._config.accessToken}`,
        'Accept': 'application/json'
      };

      // Add subscription key header if configured
      if (this._config.subscriptionKey) {
        headers['ocp-apim-subscription-key'] = this._config.subscriptionKey;
      }

      const profile = await firstValueFrom(
        this.http.get<Auth0UserProfile>(url, { headers }).pipe(
          timeout(this._config.timeout ?? 5000),
          retry(this._config.retries ?? 2),
          catchError((error: HttpErrorResponse) => {
            // Track auth errors (401/403) for potential unauthorized screen display
            if (error.status === 401 || error.status === 403) {
              this.setAuthError(error.status as 401 | 403, error);
            }
            const errorMsg = error.status === 404
              ? 'User not found'
              : `API error: ${error.status}`;
            this.cacheProfile(fgaUserId, null, errorMsg);
            return of(null);
          })
        )
      );

      if (profile) {
        // Clear auth error on successful fetch
        this.clearAuthError();
        this.cacheProfile(fgaUserId, profile);
        return this.createEnrichedUser(fgaUserId, profile);
      }

      return this.createEnrichedUser(fgaUserId, null, 'User not found');
    } finally {
      this._pendingFetches.delete(fgaUserId);
    }
  }

  /**
   * Add or update cache entry.
   */
  private cacheProfile(fgaUserId: string, profile: Auth0UserProfile | null, error?: string): void {
    this._cache.update(cache => {
      const newCache = new Map(cache);
      newCache.set(fgaUserId, {
        profile,
        fetchedAt: Date.now(),
        error
      });
      return newCache;
    });
  }

  /**
   * Create enriched user object from cache/profile.
   */
  private createEnrichedUser(
    fgaUserId: string,
    profile: Auth0UserProfile | null,
    error?: string
  ): EnrichedUser {
    let auth0UserId: string;
    try {
      auth0UserId = fgaToAuth0UserId(fgaUserId);
    } catch {
      auth0UserId = fgaUserId.startsWith('user:') ? fgaUserId.substring(5) : fgaUserId;
    }

    const displayName = profile?.upn || profile?.email || auth0UserId;

    return {
      fgaUserId,
      auth0UserId,
      email: profile?.email,
      name: profile?.name,
      displayName,
      picture: profile?.picture,
      enriched: !!profile,
      error
    };
  }

  /**
   * Check if an error is an ApiHttpError with an authentication status.
   */
  private isApiHttpAuthError(error: unknown): boolean {
    if (typeof error === 'object' && error !== null && 'status' in error && 'code' in error) {
      const apiError = error as ApiHttpError;
      return apiError.status === 401 || apiError.status === 403;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Auth Error Management
  // ---------------------------------------------------------------------------

  /**
   * Set an authorization error from a failed API call.
   * Called when Auth0 Management API returns 401 or 403.
   *
   * @param status - HTTP status code (401 or 403)
   * @param error - Original HTTP error response
   */
  private setAuthError(status: 401 | 403, error: ApiHttpError | HttpErrorResponse): void {
    const message = status === 401
      ? 'Authentication failed - invalid or expired token'
      : 'Access denied - insufficient permissions to access user data';

    this._lastAuthError.set({
      status,
      message,
      timestamp: Date.now(),
      details: error instanceof HttpErrorResponse ? error.error : (error as ApiHttpError).details
    });
  }

  /**
   * Clear any existing authorization error.
   * Called when an API request succeeds, indicating auth is working.
   */
  clearAuthError(): void {
    this._lastAuthError.set(null);
  }

}

